import { useEffect, useMemo, useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { Select } from "components/ui/Select/Select";
import { useAssignMncmsInboundMessage } from "features/pregnancy/hooks/useAssignMncmsInboundMessage";
import { usePregnancies } from "features/pregnancy/hooks/usePregnancies";
import { formatDate } from "utils/dateFormatter";

import styles from "./AssignMessageDialog.module.css";
import {
  getDefaultPregnancyId,
  NO_PREGNANCY,
} from "./defaultAssignedPregnancy";

// The autocomplete's dropdown has to clear the dialog it sits in.
const PATIENT_POPPER_Z_INDEX = 1400;

const describePregnancy = (pregnancy) => {
  if (pregnancy?.estimatedDueDate) {
    return `EDD ${formatDate(pregnancy.estimatedDueDate)}`;
  }
  if (pregnancy?.lastMenstrualPeriod) {
    return `LMP ${formatDate(pregnancy.lastMenstrualPeriod)}`;
  }

  return `Pregnancy #${pregnancy?.id}`;
};

export const AssignMessageDialog = ({ message, onClose, onAssigned }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [pregnancyId, setPregnancyId] = useState(NO_PREGNANCY);
  const [hasPickedPregnancy, setHasPickedPregnancy] = useState(false);

  const assign = useAssignMncmsInboundMessage();

  // A message that already has a patient only needs its pregnancy, so the
  // autocomplete would be asking a question that's already answered.
  const knownPatientId = message?.patientId ?? null;
  const patientId = knownPatientId ?? selectedPatient?.id ?? null;

  const { data: pregnancies, isLoading: isLoadingPregnancies } =
    usePregnancies(patientId);

  useEffect(() => {
    setSelectedPatient(null);
  }, [message?.id]);

  // A different patient means a different list, so the previous answer — and the fact that it was
  // given at all — stops applying.
  useEffect(() => {
    setPregnancyId(NO_PREGNANCY);
    setHasPickedPregnancy(false);
  }, [message?.id, patientId]);

  // Only while the field is still untouched, and only once the list has actually arrived: a default
  // appearing under a choice the user has already made would be worse than offering none.
  useEffect(() => {
    if (hasPickedPregnancy || isLoadingPregnancies || !pregnancies) return;

    setPregnancyId(getDefaultPregnancyId(pregnancies));
  }, [pregnancies, isLoadingPregnancies, hasPickedPregnancy]);

  const handlePregnancyChange = (value) => {
    setHasPickedPregnancy(true);
    setPregnancyId(value);
  };

  // The empty choice is Select's own placeholder option — adding one here too
  // would put two blank-valued entries in the list.
  const pregnancyOptions = useMemo(
    () =>
      (pregnancies ?? []).map((pregnancy) => ({
        value: String(pregnancy.id),
        label: describePregnancy(pregnancy),
      })),
    [pregnancies]
  );

  const hasPregnancies = (pregnancies ?? []).length > 0;

  const handleAssign = () => {
    if (!patientId) return;

    assign.mutate(
      {
        id: message.id,
        patientId,
        pregnancyId: pregnancyId ? Number(pregnancyId) : undefined,
      },
      { onSuccess: () => onAssigned?.() }
    );
  };

  return (
    <Dialog
      open={!!message}
      onClose={onClose}
      title="Assign message"
      width={620}
    >
      <div className={styles.content}>
        {!knownPatientId && (
          <div className={styles.field}>
            <span className={styles.label}>Patient</span>
            <PatientAutocomplete
              value={selectedPatient}
              onChange={setSelectedPatient}
              popperZIndex={PATIENT_POPPER_Z_INDEX}
              placeholder="Search for the patient..."
            />
          </div>
        )}

        <div className={styles.field}>
          <span className={styles.label}>Pregnancy</span>
          <Select
            options={pregnancyOptions}
            value={pregnancyId}
            onChange={handlePregnancyChange}
            disabled={!patientId || isLoadingPregnancies}
            placeholder="No pregnancy yet"
            fullWidth
          />
          <p className={styles.hint}>
            {hasPregnancies
              ? "Attaching the pregnancy is what allows MN-CMS sending for it."
              : "Leaving this blank is fine — assign the pregnancy once it exists here. Until then, MN-CMS sending for it stays blocked."}
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAssign}
          disabled={!patientId || assign.isPending}
        >
          {assign.isPending ? "Assigning..." : "Assign"}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
